import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {
    NumberUtils,
    provideEntityModel,
    provideReportModel,
    provideSearchModel,
    SearchResult
} from "@fedeltapointofsale/data-client";
import {ChangeDetectorRef, Component, OnDestroy} from "@angular/core";
import {AccountPaymentEntityModel} from "../account-payment-entity.model";
import {FUIAlertActions, FUIAlertService, FUIResponsiveService, FUIStackedPageContextService} from "@fedeltapointofsale/ui";
import {EditParams} from "../../../framework/entity/edit/edit-page/edit-params";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";
import {OutletReferenceData} from "../../../pos/outlet/outlet.data";
import {AccountPaymentChargeLinkSearchModel} from "../account-payment-charge-link-search.model";
import {Subscription} from "rxjs";
import {PaymentChargeLinkData} from "../account-charge-link.data";
import {AccountPaymentUnapplyAction, AccountPaymentUnapplyService} from "../account-payment-unapply.service";
import {
    AccountPaymentReceiptReportModel
} from "../../reports/account-payment-receipt-report/account-payment-receipt-report.model";
import {EditPageActionEvent} from "../../../framework/entity/edit/edit-page/edit-page-action.event";
import {CurrencyPipe} from "@angular/common";

export class AccountPaymentEditParams extends EditParams {

    constructor(identifier: any, editMode: EditMode, public accountId: string, public accountName: string) {
        super(identifier, editMode, null);
    }
}

@Component({
    selector: "wbo-account-payment-edit-page",
    templateUrl: "./account-payment-edit-page.component.html",
    providers: [provideReportModel(AccountPaymentReceiptReportModel), (provideEntityModel(AccountPaymentEntityModel)), (provideSearchModel(AccountPaymentChargeLinkSearchModel)), AccountPaymentUnapplyService]
})
export class AccountPaymentEditPageComponent extends AbstractEditPage implements OnDestroy {

    totalApplied: number = 0;
    discrepancy: number = 0;
    totalCredit: number = 0;

    startingCredit: number = 0;
    startingApplied: number = 0;

    chargePayments: PaymentChargeLinkData[] = [];

    /**
     * Payments that have already been applied to a charge
     */
    appliedChargePayments: PaymentChargeLinkData[] = [];

    /**
     * Indicates that the payment is in edit mode. Edit is only used to apply existing credit
     */
    editMode: boolean;

    disableAction: boolean = true;

    /**
     * Indicates if charges can be applied
     */
    canApply: boolean = false;

    status: string;
    type: string;

    /**
     * Indicates that the data reload was triggered from an unapply payment
     */
    loadedFromUnapply: boolean;

    outlet: OutletReferenceData = new OutletReferenceData();

    private subscriptions: Subscription = new Subscription();

    constructor(public model: AccountPaymentEntityModel,
                public stackedPageContextService: FUIStackedPageContextService,
                private chargesModel: AccountPaymentChargeLinkSearchModel,
                private unapplyService: AccountPaymentUnapplyService,
                private receiptReportModel: AccountPaymentReceiptReportModel,
                private alertService: FUIAlertService,
                protected changeDetect: ChangeDetectorRef,
                responsiveService: FUIResponsiveService,
                private currencyPipe: CurrencyPipe) {
        super(changeDetect, responsiveService);

        if (!stackedPageContextService) {
            console.error("AccountPaymentEditPageComponent must be accessed via FUIStackedPageContextService providing AccountPaymentEditParams");
        }
        this.editMode = stackedPageContextService.params.editMode === EditMode.EDIT;
        model.data.account.details.accountName = stackedPageContextService.params.accountName;
        model.data.account.accountId = stackedPageContextService.params.accountId;
        chargesModel.filter["accountId"] = stackedPageContextService.params.accountId;
        chargesModel.limit = 500;
        this.subscriptions.add(this.model.$data.subscribe(() => {
            this.totalApplied = 0;
            this.discrepancy = 0;
            this.totalCredit = 0;

            this.startingCredit = 0;
            this.startingApplied = 0;
            this.chargePayments = [];
            this.appliedChargePayments = [];
            this.disableAction = true;
            this.canApply = false;


            // Unset since the server does not know about these fields
            this.status = this.model.data.status;
            this.type = this.model.data.type;
            this.model.data.status = undefined;
            this.model.data.type = undefined;

            if (!this.editMode) {
                this.model.data.details.paymentDate = new Date();
            }

            if (this.model.data.details.creditAmt) {
                this.startingCredit = this.model.data.details.creditAmt;
            }

            this.model.data.chargePayments.forEach((c: PaymentChargeLinkData) => {
                if (c.amtUsed) {
                    this.startingApplied = NumberUtils.roundValue(c.amtUsed, 2);
                }
            });
            this.updateTotals();


            this.subscriptions.add(chargesModel.search().subscribe((result: SearchResult) => {
                result.data.forEach((c: PaymentChargeLinkData) => this.model.data.chargePayments.push(c));
                this.model.data.chargePayments.forEach((c: PaymentChargeLinkData) => {
                    if (c.linkId) {
                        this.appliedChargePayments.push(c);
                    } else {
                        this.chargePayments.push(c);
                    }
                });

                if (!this.editMode || (this.status !== "voided" && this.type !== "void" && this.startingCredit > 0 && this.chargePayments.length != 0)) {
                    this.disableAction = false;
                    this.canApply = true;
                    this.changeDetect.detectChanges();
                }
                this.updateTotals();
            }));

            if (this.loadedFromUnapply) {
                this.loadedFromUnapply = false;
                this.alertService.showInformation("wbo.account-payment.edit.prompt.unapplyComplete.heading",
                    "wbo.account-payment.edit.prompt.unapplyComplete.message").subscribe(() => {
                });
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onSave(event: EditPageActionEvent): void {
        if (!this.editMode) {
            this.model.data.details.outletId = this.outlet.outletId;
            this.model.data.details.outletName = this.outlet.name;

            if (this.totalCredit > 0 && this.totalApplied === 0) {
                event.actionComplete = new Promise((resolve) => {
                    this.alertService.showConfirmationOkCancel(
                        "wbo.account-payment.edit.prompt.paymentNotApplied.heading",
                        "wbo.account-payment.edit.prompt.paymentNotApplied.message",
                        {creditAmount: this.currencyPipe.transform(this.totalCredit, "USD", true, "1.2-2")}
                    ).subscribe((action) => resolve(action === FUIAlertActions.OK_ACTION));
                });
            }
        }
    }


    /**
     * Apply payment amount to a single charge
     * @param {PaymentChargeLinkData} charge
     */
    applyPaymentToCharge(charge: PaymentChargeLinkData): void {
        charge.amtUsed = undefined;
        charge.creditAmt = undefined;
        this.updateTotals();

        if (charge.charge.amtDue < 0) {

            // for negative, the full amount or nothing can be applied
            let remaining: number = this.amountRemaining();
            if (remaining > charge.charge.amtDue) {
                return;
            }

            remaining = NumberUtils.roundValue(remaining - charge.charge.amtDue, 2);

            if (remaining <= 0) {
                charge.amtUsed = charge.charge.amtDue;
            } else {
                // Unable to allocate the full amount
                charge.amtUsed = undefined;
            }
        } else {

            const remaining: number = this.amountRemaining();

            if (remaining <= 0) {
                return;
            }
            charge.amtUsed = (remaining - charge.charge.amtDue < 0) ? remaining : charge.charge.amtDue;
        }

        this.applyCreditToCharge(charge);
    }

    chargeUpdated(charge: PaymentChargeLinkData): void {
        if (charge.charge.amtDue < 0 && charge.amtUsed !== charge.charge.amtDue) {
            charge.amtUsed = undefined;
        }

        if (charge.charge.amtDue > 0 && charge.amtUsed && charge.amtUsed < 0) {
            charge.amtUsed = undefined;
        }

        this.applyCreditToCharge(charge);
    }

    /**
     * Applies credit after the payment has been applied
     */
    applyCreditToCharge(charge: PaymentChargeLinkData): void {
        charge.creditAmt = 0;

        if (charge.charge.amtDue > 0 && charge.amtUsed && charge.amtUsed > 0) {
            const amount = NumberUtils.roundValue(charge.amtUsed - charge.charge.amtDue, 2);
            if (amount > 0) {
                charge.creditAmt = amount;
            }
        }

        this.updateTotals();
    }

    amountUpdated(): void {
        this.startingCredit = this.model.data.details.paymentAmt;
        this.updateTotals();
    }

    updateTotals(): void {
        this.totalApplied = this.calcTotalApplied();
        this.discrepancy = this.calcDiscrepancy();
        this.totalCredit = this.calcTotalCredit();
    }

    unapplyPayment(): void {
        this.alertService.showConfirmationOkCancel("wbo.account-payment.edit.prompt.unapply.heading",
            "wbo.account-payment.edit.prompt.unapply.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.unapplyService.performAction(new AccountPaymentUnapplyAction(this.model.data.account.accountId, this.model.data.details.receiptNo)).then(() => {
                    this.loadedFromUnapply = true;
                    this.editPageComponent.reload();
                });
            }
        });
    }

    generateReceipt(): void {
        if (!this.editPageComponent.reportGenerationService.running) {
            this.receiptReportModel.data.paymentId = this.model.data.paymentId;
            this.receiptReportModel.data.outletId = this.model.data.details.outletId;
            this.editPageComponent.reportGenerationService.execute(this.receiptReportModel);
        }
    }

    /**
     * Retrieves the amount remaining of un-applied payment
     */
    private amountRemaining(): number {
        if (!this.model.data.details.paymentAmt) {
            return 0;
        }

        let amount = NumberUtils.roundValue(this.model.data.details.paymentAmt - this.totalApplied, 2);
        if ((this.model.data.details.paymentAmt > 0 && amount < 0) || (this.model.data.details.paymentAmt < 0 && amount > 0)) {
            amount = 0;
        }

        return amount;
    }

    /**
     * Retrieves difference between amount and amount applied
     */
    private calcDiscrepancy(): number {
        if (this.totalApplied === 0) {
            return 0;
        }

        let amount: number = 0;
        if (this.model.data.details.paymentAmt) {
            amount = this.model.data.details.paymentAmt;
        }

        return NumberUtils.roundValue(amount - this.totalApplied, 2);
    }

    /**
     * Retrieves the total amount applied
     */
    private calcTotalApplied(): number {
        let amount: number = this.startingApplied;
        this.chargePayments.forEach((c: PaymentChargeLinkData) => {
            if (c.amtUsed) {
                amount = NumberUtils.roundValue(amount + c.amtUsed, 2);
            }
        });

        return amount;
    }

    private calcTotalCredit(): number {
        let amount: number = this.startingCredit;
        this.chargePayments.forEach((c: PaymentChargeLinkData) => {
            if (c.creditAmt) {
                amount = NumberUtils.roundValue(amount + c.creditAmt, 2);
            }

            if (c.amtUsed) {
                amount = NumberUtils.roundValue(amount - c.amtUsed, 2);
            }
        });
        return amount;
    }
}
